/** retry の既定の最大試行回数 */
const DEFAULT_MAX_RETRIES = 3

/** 401 retry の待機時間を計算する倍率ミリ秒 */
const RETRY_DELAY_MULTIPLIER_MS = 3000

/** EVM explorer API の rate limit retry で待機するミリ秒 */
const EVM_EXPLORER_RATE_LIMIT_RETRY_DELAY_MS = 3000

/** EVM explorer API の 3 calls/sec rate limit error message */
const EVM_EXPLORER_RATE_LIMIT_MESSAGE =
  'Max calls per sec rate limit reached (3/sec)'

/** retry 対象処理の待機関数 */
type Sleep = (params: { ms: number }) => Promise<void>

/** retryWith401Handler のパラメータ */
type RetryWith401HandlerParameters<T> = {
  /** retry 付きで実行する非同期処理 */
  fn: () => Promise<T>
  /** 最大 retry 回数 */
  maxRetries?: number
  /** retry 前の待機処理 */
  sleepMs?: Sleep
}

/**
 * 401 系の retry 対象 error か判定する
 * @param params - 判定対象 error
 * @returns retry 対象なら true
 */
const isRetriable401Error = (params: { error: unknown }): boolean => {
  const { error } = params
  return error instanceof Error && error.message.includes('Status: 401')
}

/**
 * EVM explorer API の 3 calls/sec rate limit error か判定する
 * @param params - 判定対象 error
 * @returns retry 対象なら true
 */
const isEvmExplorerRateLimitError = (params: { error: unknown }): boolean => {
  const { error } = params
  return (
    error instanceof Error &&
    !error.message.includes('Status: 429') &&
    error.message.includes(EVM_EXPLORER_RATE_LIMIT_MESSAGE)
  )
}

/**
 * retry 前に待機するミリ秒を返す
 * @param params - error と retry 回数
 * @returns 待機ミリ秒
 */
const getRetryDelayMs = (params: {
  error: unknown
  retryCount: number
}): number => {
  const { error, retryCount } = params
  if (isEvmExplorerRateLimitError({ error })) {
    return EVM_EXPLORER_RATE_LIMIT_RETRY_DELAY_MS
  }
  return Math.min(12_000, 2 ** (retryCount - 1) * RETRY_DELAY_MULTIPLIER_MS)
}

/**
 * 指定ミリ秒だけ待機する Promise を作成する
 * @param params - 待機ミリ秒
 * @returns 待機完了時に resolve される Promise
 */
const sleep = (params: { ms: number }): Promise<void> => {
  const { ms } = params
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * 401 error と EVM explorer API rate limit error を retry しながら非同期処理を実行する
 * @template T - retry 対象処理の戻り値型
 * @param params - retry 対象処理と retry 設定
 * @returns retry 対象処理の実行結果
 * @throws retry 対象外 error または最大 retry 回数を超過した error
 */
export const retryWith401Handler = async <T>(
  params: RetryWith401HandlerParameters<T>,
): Promise<T> => {
  const { fn, maxRetries = DEFAULT_MAX_RETRIES, sleepMs = sleep } = params
  let retryCount = 0

  while (retryCount <= maxRetries) {
    try {
      return await fn()
    } catch (error) {
      const shouldRetry =
        isRetriable401Error({ error }) || isEvmExplorerRateLimitError({ error })
      if (retryCount < maxRetries && shouldRetry) {
        retryCount += 1
        await sleepMs({ ms: getRetryDelayMs({ error, retryCount }) })
        continue
      }
      throw error
    }
  }

  throw new Error('Maximum retry attempts reached')
}
